import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import type { StudioSignInResponse } from "@/lib/api";
import type { Pack } from "@/components/pricing/PackCard";
import { useAuth } from "@/contexts/useAuth";
import { trackEvent } from "@/lib/analytics";
import { isTelegramSession, rememberTelegramSession, takeStashedToken } from "@/lib/signin-stash";
import { pageSignature } from "@/lib/scroll-memory";

// BUY CREDITS ON THE LANDING PAGE: everything the credits section
// (components/landing/CreditPacksSection.tsx) does besides drawing.
//
// Three ways in:
//   * a website visitor: signed in, Buy goes to Stripe; signed out, Buy goes
//     to /signup?redirect_to=/?pack=<type>&resume=1#credits and, back here
//     with a session, checkout starts on its own, once;
//   * the Telegram studio (Mini App): /?from=studio&pack=&lang=&signin=<one-time
//     token>#credits, the token traded at /api/web/user/studio-signin;
//   * the Python bot: /?from=bot&needed=&token=<signed token>#credits, the
//     token traded at /api/web/user/bot-signin.
// index.html moves both tokens out of the address bar before any analytics
// runs; they are read from there first.
//
// The safety rules are /pricing's (pages/Pricing.tsx): a failed sign-in buys
// nothing, not even on a session this browser already had; a Telegram visit
// is never sent to a website sign-up; a 401 at checkout on a Telegram visit
// is the same dead end as a failed sign-in. Checkout from Telegram sends
// return_to 'studio' (or 'bot' for the bot), so Stripe returns to the
// one-button /billing/success, and a cancel comes back as the same visit.
//
// A TELEGRAM VISIT BUYS ONLY AS THE ACCOUNT TELEGRAM SIGNED IN. Whatever
// session this browser already held may be another account's (a shared
// phone, a website account never linked to that Telegram id): buying on it
// would credit a balance Telegram never reads, under a line saying the
// opposite. So a Telegram visit counts as signed in only after this visit's
// token exchange, or with the session such an exchange gave this tab earlier
// (a cancelled checkout coming back, signin-stash isTelegramSession).
// Otherwise: from the studio, back to Telegram for a signed link; from the
// bot, whose link carries no token when the bot has no signing secret, the
// page is simply the website's (sign in or up, buy for that account, no
// Telegram claims). A browser already signed in to a website account is asked
// before a Telegram link replaces that session.

export type SigninState = "none" | "working" | "done" | "failed" | "unavailable" | "confirm";

/** Where the visit came from: the website, the Telegram studio (Mini App), or the bot. */
export type CheckoutVisit = "web" | "studio" | "bot";

export type SignedInAccount = StudioSignInResponse["user"];

export const CREDITS_ANCHOR = "credits";

/** Where a signed-out buyer comes back to after signing up: the pack picked, checkout resumed. */
export const resumePath = (packType: string) =>
  `/?pack=${encodeURIComponent(packType)}&resume=1#${CREDITS_ANCHOR}`;

/**
 * Whether the landing page was opened for its credits section. An anchor of
 * its own wins; with no anchor, a Telegram visit, a pick or a cancelled
 * checkout still counts. The section scrolls these itself once its packs are
 * in, and Index.tsx leaves them alone.
 */
export function isCreditsVisit(search: string, hash: string): boolean {
  if (hash && hash !== "#") return hash === `#${CREDITS_ANCHOR}` || hash.startsWith("#pack-");
  const q = new URLSearchParams(search);
  const from = q.get("from");
  return from === "studio" || from === "bot" || q.get("checkout") === "cancelled" || q.has("pack");
}

// BACK FROM STRIPE CHECKOUT loads the landing page afresh, and a fresh
// landing page opens on its welcome stop: a buyer who bought from another
// stop came back to the welcome cards at the top, not to the packs (scroll
// review 2026-10-03). So the address left behind names the stop (?tool=)
// and the packs (#credits): Back reopens that stop where it was left
// (ScrollManager), and failing that, on the packs (isCreditsVisit).
export function markCheckoutReturn() {
  try {
    const url = new URL(window.location.href);
    const stop = /^landing:([\w-]+)$/.exec(pageSignature())?.[1];
    if (stop) url.searchParams.set("tool", stop);
    url.hash = `#${CREDITS_ANCHOR}`;
    // The entry's own state (its key) stays: that is where its scroll is kept.
    window.history.replaceState(window.history.state, "", url.toString());
  } catch { /* the address stays as it was */ }
}

export function useCreditCheckout() {
  const { i18n } = useTranslation();
  const { refreshUser, user, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Read once: the tokens leave the address bar below, and the visit stays a
  // Telegram visit whatever the address says afterwards.
  const [entry] = useState(() => {
    const q = new URLSearchParams(location.search);
    const from = q.get("from");
    const stashedStudio = takeStashedToken("studio_signin");
    const studioToken = q.get("signin") || stashedStudio;
    const botToken = from === "bot" ? takeStashedToken("bot_signin") || q.get("token") : null;
    const visit: CheckoutVisit = from === "bot" ? "bot" : from === "studio" || studioToken ? "studio" : "web";
    return { visit, token: visit === "bot" ? botToken : studioToken, lang: q.get("lang") };
  });

  // With no token, the session in this browser counts for Telegram only when
  // a Telegram sign-in gave it to this tab.
  const [signin, setSignin] = useState<SigninState>(() => {
    if (entry.token) return "working";
    if (entry.visit === "web") return "none";
    if (isTelegramSession(api.getToken())) return "done";
    return entry.visit === "studio" ? "failed" : "none";
  });
  // "Stay signed in as <email>": the Telegram link was turned down.
  const [declined, setDeclined] = useState(false);

  const params = new URLSearchParams(location.search);
  const from = params.get("from");
  const asked: CheckoutVisit = entry.visit !== "web" ? entry.visit
    : from === "bot" ? "bot" : from === "studio" ? "studio" : "web";
  // A bot link that proved nothing (no token, no Telegram session in this
  // tab), or a declined one, is a website visit.
  const visit: CheckoutVisit = declined || (asked === "bot" && !entry.token && signin !== "done") ? "web" : asked;
  const telegram = visit !== "web";
  const wanted = params.get("pack");
  const neededRaw = Number(params.get("needed"));
  const needed = Number.isFinite(neededRaw) && neededRaw > 0 ? Math.ceil(neededRaw) : null;
  const cancelled = params.get("checkout") === "cancelled";

  const [account, setAccount] = useState<SignedInAccount | null>(null);
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [buying, setBuying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Takes `keys` out of the address, keeping the anchor (setSearchParams would drop it). */
  const strip = useCallback((keys: string[]) => {
    const q = new URLSearchParams(location.search);
    if (!keys.some((k) => q.has(k))) return;
    keys.forEach((k) => q.delete(k));
    const search = q.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    Promise.resolve()
      .then(() => api.getPacks())
      .then((all) => {
        if (!mounted.current) return;
        setPacks((Array.isArray(all) ? all : [])
          .filter((p) => p && p.kind === "credits")
          .sort((a, b) => a.points - b.points));
      })
      .catch(() => { if (mounted.current) setLoadError(true); })
      .finally(() => { if (mounted.current) setLoading(false); });
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // The studio's or the bot's language, so a Spanish customer is not handed English.
  useEffect(() => {
    const lang = entry.lang;
    if ((lang === "en" || lang === "es") && !String(i18n.language || "").startsWith(lang)) {
      void i18n.changeLanguage(lang);
    }
  }, [entry, i18n]);

  // SIGN IN WITH THE ONE-TIME TOKEN. It leaves the address bar first, and
  // replaces the session this browser had: the credits must land on the
  // account Telegram uses. A 503 (sign-in not set up here) is told apart from
  // an expired link: a new link would fail the same way.
  const exchange = useCallback(async () => {
    const token = entry.token;
    if (!token) return;
    setSignin("working");
    try {
      const r = entry.visit === "bot" ? await api.botSignIn(token) : await api.studioSignIn(token);
      if (r.success && r.data?.token) {
        setAccount(r.data.user);
        await refreshUser();
        setSignin("done");
        trackEvent(`${entry.visit}_signin_completed`, { pack_type: wanted || null, surface: "landing" });
      } else {
        setSignin(r.status === 503 || r.error === "signin_unavailable" ? "unavailable" : "failed");
        trackEvent(`${entry.visit}_signin_failed`, { reason: r.error || null, surface: "landing" });
      }
    } catch {
      setSignin("failed");
    }
  }, [entry, refreshUser, wanted]);

  // Once the browser's own session is known: a website account signed in
  // here (not a studio account, not a session Telegram gave this tab) is
  // asked first (continueWithTelegram / stayAsWebsite).
  const signinStarted = useRef(false);
  useEffect(() => {
    if (!entry.token || signinStarted.current || authLoading) return;
    signinStarted.current = true;
    strip(entry.visit === "bot" ? ["token", "lang"] : ["signin", "lang"]);
    const held = api.getToken();
    if (held && user && !user.studio_account && !isTelegramSession(held)) {
      setSignin("confirm");
      return;
    }
    void exchange();
    // Runs once per visit; `entry` is fixed at first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  const continueWithTelegram = useCallback(() => {
    if (signin !== "confirm") return;
    trackEvent(`${entry.visit}_signin_switch`, { choice: "telegram", surface: "landing" });
    void exchange();
  }, [signin, entry, exchange]);

  const stayAsWebsite = useCallback(() => {
    if (signin !== "confirm") return;
    trackEvent(`${entry.visit}_signin_switch`, { choice: "website", surface: "landing" });
    setDeclined(true);
    setSignin("none");
  }, [signin, entry]);

  const buy = useCallback(async (packType: string) => {
    // A failed sign-in buys nothing: the session left in this browser may be
    // another account's, and Checkout would go ahead on it.
    if (signin !== "none" && signin !== "done") return;
    // Nor does a Telegram visit that no Telegram sign-in proved.
    if (telegram && signin !== "done") {
      setSignin("failed");
      return;
    }
    if (!api.getToken()) {
      // From Telegram there is no sign-up to send anyone to: a website
      // account made here would not be the one Telegram shows.
      if (telegram) {
        setSignin("failed");
        return;
      }
      navigate(`/signup?redirect_to=${encodeURIComponent(resumePath(packType))}`);
      return;
    }
    setBuying(packType);
    setError(null);
    try {
      const returnTo = visit === "bot" ? ("bot" as const) : visit === "studio" ? ("studio" as const) : null;
      const { url } = await api.createCheckout({ pack_type: packType, ...(returnTo ? { return_to: returnTo } : {}) });
      const selected = packs.find((p) => p.type === packType);
      trackEvent("checkout_started", {
        checkout_flow: "credit_pack",
        pack_type: packType,
        pack_kind: selected?.kind,
        credit_amount: selected?.points,
        currency: selected?.currency,
        source: visit,
        surface: "landing",
      });
      markCheckoutReturn();
      window.location.href = url; // Stripe-hosted checkout
    } catch (e: unknown) {
      const failure = (e && typeof e === "object" ? e : {}) as { status?: number; code?: string; message?: string };
      // A refused session on a Telegram visit (expired, or an account that
      // was folded into another): back to Telegram for a fresh link.
      if (telegram && failure.status === 401) {
        rememberTelegramSession(null);
        setSignin("failed");
        setBuying(null);
        return;
      }
      setError(failure.code || failure.message || "");
      setBuying(null);
    }
  }, [signin, telegram, navigate, packs, visit]);

  // BACK FROM SIGN-UP OR LOG-IN: /?pack=<type>&resume=1 with a session starts
  // that checkout once. `resume` leaves the address either way, so a reload or
  // the back button never starts a second one.
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current || loading || authLoading || signin === "working") return;
    const q = new URLSearchParams(location.search);
    if (q.get("resume") !== "1") return;
    resumed.current = true;
    strip(["resume"]);
    const packType = q.get("pack");
    if (!packType || !api.getToken() || !packs.some((p) => p.type === packType)) return;
    trackEvent("checkout_resumed", { pack_type: packType, surface: "landing" });
    void buy(packType);
  }, [loading, authLoading, signin, location.search, packs, buy, strip]);

  // The exchange says whether this is a studio account; a later visit (a
  // cancelled checkout coming back) has only /api/auth/me to go on.
  const studioAccount = account ? Boolean(account.studio_account) : Boolean(user?.studio_account);

  return {
    visit,
    telegram,
    wanted,
    needed,
    cancelled,
    signin,
    account,
    setAccount,
    studioAccount,
    packs,
    loading,
    loadError,
    reload,
    buying,
    error,
    buy,
    continueWithTelegram,
    stayAsWebsite,
    /** Held while signing in or asking, and for good once it failed (see buy()). */
    locked: signin !== "none" && signin !== "done",
  };
}
